import { useMemo } from 'react'

import { AxiosResponse } from 'axios'

import { Episode } from '../../../models/episodes/Episodes'
import { RiskFactorsResponse } from '../../../models/riskFactors/RiskFactors'
import { SearchRequestData, TriageSearch } from '../../../models/searchForCare/SearchForCare'
import { CENTER_OF_EXCELLENCE } from '../../centerOfExcellence/types'
import { COMMON_MIXPANEL_PAGE } from '../../common/constants/mixpanelConstants'
import { isLanguageEnglish } from '../../intl/utils/isLanguageEnglish'
import { useRunSpeculativeSearch } from '../../searchForCare/hooks/useRunSpeculativeSearch'
import { SentryLogLevelInput } from '../../sentry/utils'
import { DEFAULT_SPECULATIVE_FILTER_THRESHOLDS, WFMH_TOOLKIT_TO_PROVIDER_SUPPLY_GROUP } from '../constants'
import { SpeculativeSearchThresholds, TOOLKIT_TYPE, WELLNESS_TOOLKIT_PROMOTION } from '../types'
import {
  evaluateShortCircuitEligibility,
  evaluateToolkitAvailability,
  getWellnessToolkitAvailableConfig,
  isLatestCompletedSearchNotTierThree,
  isNotInActiveEpisode as isNotInActiveEpisodeCheck,
  isNotSelfHarmOrSuicialIdeationIndicated,
} from '../wellnessToolkitUtil'

export interface useIsWellnessToolkitAvailableParams {
  toolkit: TOOLKIT_TYPE
  page?: COMMON_MIXPANEL_PAGE
  lyraId: string
  isUserTeen: boolean
  triageSearches: TriageSearch[]
  episodes: Episode[]
  isUserLoggedIn: boolean
  isInternationalUser: boolean
  postSpeculativeSearch: (lyraId: string, searchData: SearchRequestData) => Promise<AxiosResponse<boolean>>
  isCoachingEnabled: boolean
  isTherapyEnabled: boolean
  isCoachingForParentsEnabled: boolean
  riskFactors?: RiskFactorsResponse
  userDOB?: string
  userCountryName?: string
  isEssentialsEnabled: boolean
  hideDependentField?: boolean
  hideChildSearchCTA?: boolean
  isFamilyHubEnabled?: boolean
  isCoachingEntryPointEnabled?: boolean
  isCoachingForParentsEntryPointEnabled?: boolean
  isCriticalIncidentSupportToolkitEnabled?: boolean
  isNeurodiversityCOEEnabled?: boolean
  isManagerHubEnabled?: boolean
  displayLanguage?: string
  isSpeculativeFilterEnabled: boolean
  speculativeSearchThresholds?: SpeculativeSearchThresholds
  logger?: (data: { message: string; type: SentryLogLevelInput }) => void
  route?: string
  /**
   * Specifies which validations to override. E.g. in some cases toolkit availability may be used for different entrypoints
   * on the same screen where one entrypoint does require specific validation while the other does not. Having overrides allows
   * more complex validation for a specific check to happen at the parent level and avoids redundant speculative search calls.
   */
  validationOverrides?: { shouldEssentialsBeEnabled?: boolean } // can add more overrides as needed
  isLyraSelect: boolean
  disableSelfCareTopics?: string
}

/**
 * Use the app specific version of this hook.
 *
 * Conditions for when a toolkit is available: https://lyrahealth.atlassian.net/wiki/spaces/EN/pages/3448995850/Wellness+Toolkits+Coaching+Parenting+Family+Hub
 * @param toolkitType
 * @param page Where the toolkit link is being shown. Defaults to homebase carousel
 *
 * The remaining params are all app specific.
 *
 * @returns `isToolkitAvailable` - if toolkit can be shown
 * @returns `isLoading` - if still evaluating the conditions
 */
export const useIsWellnessToolkitAvailable = ({
  toolkit,
  page,
  lyraId,
  isUserTeen,
  triageSearches,
  isCoachingEnabled,
  isTherapyEnabled,
  isCoachingForParentsEnabled,
  riskFactors,
  episodes,
  isUserLoggedIn,
  isInternationalUser,
  postSpeculativeSearch,
  userDOB,
  userCountryName,
  isEssentialsEnabled,
  hideDependentField,
  hideChildSearchCTA,
  isFamilyHubEnabled,
  isCoachingEntryPointEnabled,
  isCoachingForParentsEntryPointEnabled,
  isCriticalIncidentSupportToolkitEnabled,
  isNeurodiversityCOEEnabled,
  isManagerHubEnabled,
  displayLanguage,
  isSpeculativeFilterEnabled,
  speculativeSearchThresholds = DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
  route,
  logger,
  validationOverrides,
  isLyraSelect,
  disableSelfCareTopics,
}: useIsWellnessToolkitAvailableParams) => {
  const conditions = useMemo(() => {
    return {
      ...getWellnessToolkitAvailableConfig({ toolkit, isLyraSelect, page }),
      ...(validationOverrides || {}),
    }
  }, [isLyraSelect, page, toolkit, validationOverrides])
  const { configExtras } = conditions

  const isUserDisplayLanguageEnglish = displayLanguage ? isLanguageEnglish(displayLanguage) : false

  const featureFlagByToolkit: Partial<Record<TOOLKIT_TYPE, boolean>> = {
    [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: isFamilyHubEnabled,
    [WELLNESS_TOOLKIT_PROMOTION.WORK]: isCoachingEntryPointEnabled,
    [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: isCoachingEntryPointEnabled,
    [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: isCoachingEntryPointEnabled,
    [WELLNESS_TOOLKIT_PROMOTION.PARENT]: isCoachingForParentsEntryPointEnabled,
    [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: isCriticalIncidentSupportToolkitEnabled,
    [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: isNeurodiversityCOEEnabled,
    [CENTER_OF_EXCELLENCE.MANAGER_HUB]: isManagerHubEnabled,
  }

  const isFeatureFlagEnabled = featureFlagByToolkit[toolkit]

  const { hasPassedShortCircuitEligibilityChecks } = useMemo(
    () =>
      evaluateShortCircuitEligibility({
        toolkitLabel: toolkit,
        conditions,
        configExtras,
        isFeatureFlagEnabled,
        isEssentialsEnabled,
        isCoachingEnabled,
        isTherapyEnabled,
        isCoachingForParentsEnabled,
        hideChildSearchCTA,
        hideDependentField,
        isUserTeen,
        isInternationalUser,
        isUserDisplayLanguageEnglish,
        disableSelfCareTopics,
      }),
    [
      conditions,
      configExtras,
      hideChildSearchCTA,
      hideDependentField,
      isCoachingEnabled,
      isCoachingForParentsEnabled,
      isEssentialsEnabled,
      isFeatureFlagEnabled,
      isInternationalUser,
      isTherapyEnabled,
      isUserDisplayLanguageEnglish,
      isUserTeen,
      toolkit,
      disableSelfCareTopics,
    ],
  )

  // Speculative search (skip if programSearchRequestData is undefined)
  const shouldHaveProviderSupply = conditions.shouldHaveProviderSupply && isUserLoggedIn
  const program = configExtras?.programSearchRequestData
  const { clientele, treatment, partner, offering, directLinkIntent } = program ?? {}
  const searchRequestData = useMemo(() => {
    return hasPassedShortCircuitEligibilityChecks && program && shouldHaveProviderSupply
      ? { clientele, treatment, partner, offering, directLinkIntent }
      : undefined
  }, [
    clientele,
    directLinkIntent,
    hasPassedShortCircuitEligibilityChecks,
    offering,
    partner,
    program,
    shouldHaveProviderSupply,
    treatment,
  ])
  const providerSupplyGroup = !isLyraSelect ? WFMH_TOOLKIT_TO_PROVIDER_SUPPLY_GROUP[toolkit] : undefined
  const toolKitFilter = providerSupplyGroup && speculativeSearchThresholds[providerSupplyGroup]
  const speculativeSearchResult = useRunSpeculativeSearch({
    searchRequestData,
    isUserLoggedIn,
    lyraId,
    userDOB,
    userCountryName,
    isInternationalUser,
    postSpeculativeSearch,
    isSpeculativeFilterEnabled,
    ...(isSpeculativeFilterEnabled && { filters: toolKitFilter }),
  })
  const hasAvailableProviderSupply = speculativeSearchResult.hasAvailableSupply

  const isNotSHOrSI = isNotSelfHarmOrSuicialIdeationIndicated(riskFactors)

  const isNotT3Recommendation = isLatestCompletedSearchNotTierThree(triageSearches)

  const isNotInActiveEpisode = isNotInActiveEpisodeCheck(
    episodes,
    lyraId,
    configExtras?.allowedProgramsForActiveEpisodes,
  )

  const { isToolkitAvailable, reasons } = useMemo(
    () =>
      evaluateToolkitAvailability({
        toolkitLabel: toolkit,
        conditions,
        configExtras,
        isFeatureFlagEnabled,
        isEssentialsEnabled,
        isCoachingEnabled,
        isTherapyEnabled,
        isCoachingForParentsEnabled,
        hideChildSearchCTA,
        hideDependentField,
        isUserTeen,
        isInternationalUser,
        isUserDisplayLanguageEnglish,
        disableSelfCareTopics,
        isNotSHOrSI,
        isNotInActiveEpisode,
        isNotT3Recommendation,
        shouldHaveProviderSupply,
        hasAvailableProviderSupply,
      }),
    [
      toolkit,
      conditions,
      configExtras,
      disableSelfCareTopics,
      isFeatureFlagEnabled,
      isEssentialsEnabled,
      isCoachingEnabled,
      isTherapyEnabled,
      isCoachingForParentsEnabled,
      hideChildSearchCTA,
      hideDependentField,
      isUserTeen,
      isInternationalUser,
      isUserDisplayLanguageEnglish,
      isNotSHOrSI,
      isNotInActiveEpisode,
      isNotT3Recommendation,
      shouldHaveProviderSupply,
      hasAvailableProviderSupply,
    ],
  )

  const isToolkitAvailableInfo = useMemo(() => {
    const isLoading = speculativeSearchResult.isLoading

    if (!isLoading) {
      const debugMessage = `Is ${toolkit} toolkit available? ${isToolkitAvailable ? 'yes' : 'no'}
(Route: ${route}) 
Conditions (available if all are true):
`
      const conditions = reasons.map((item) => {
        return { condition: item.propertyName, evaluatedTo: item.value }
      })
      console.debug(debugMessage, conditions)
      if (logger) {
        logger({ message: debugMessage + JSON.stringify(conditions), type: 'INFO' })
      }
    }

    return {
      isToolkitAvailable,
      isLoading,
      reasonsForAvailabilityStatus: reasons,
    }
  }, [speculativeSearchResult.isLoading, isToolkitAvailable, toolkit, route, reasons, logger])

  return isToolkitAvailableInfo
}
